import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, Observable, catchError, map, switchMap, tap, throwError } from 'rxjs';

import { MpsUser } from '../../app/models/mps-user.model';
import { MpsILaravelDefaultResponse } from '../interfaces/mps-responses.interface';
import { MpsOauth2Provider } from '../models/mps-provider.model';
import { mpsEndpoints } from '../mps-endpoints';
import { mpsRouteNames } from '../mps-route-names';
import { MpsApiService } from './mps-api.service';
import { MpsOauth2Service } from './mps-oauth2.service';

@Injectable({
  providedIn: 'root',
})
export class MpsUserService {
  private userSubject = new BehaviorSubject<MpsUser | null>(null);
  private apiService = inject(MpsApiService);
  private oauth2Service = inject(MpsOauth2Service);
  private router = inject(Router);

  public fetchUser(): Observable<MpsUser> {
    return this.apiService.get<{ data: MpsUser }>(mpsEndpoints.user).pipe(
      catchError((error) => {
        if (error.status === 401) {
          const provider = localStorage.getItem('provider');

          if (!provider) {
            this.getProviders();
          } else {
            return this.refreshToken(provider);
          }
        }
        return throwError(() => error);
      }),
      map((resp: { data: MpsUser }) => {
        const user = resp.data;
        this.userSubject.next(user);
        this.storeUserLanguage(user);
        return user;
      })
    );
  }

  public logout(): Observable<MpsILaravelDefaultResponse<boolean>> {
    return this.apiService.get<MpsILaravelDefaultResponse<boolean>>(mpsEndpoints.logout).pipe(
      tap(() => {
        this.userSubject.next(null);
        localStorage.removeItem('token');
      })
    );
  }

  public isLoggedIn(): boolean {
    return this.getUserSubject().value !== null;
  }

  public getUserSubject(): BehaviorSubject<MpsUser | null> {
    return this.userSubject;
  }

  public getUser(): MpsUser | null {
    return this.userSubject.value;
  }

  public changeLang(userId: number, lang: string): Observable<MpsUser> {
    return this.apiService
      .post<
        { lang: string },
        MpsILaravelDefaultResponse<MpsUser>
      >(mpsEndpoints.changeLang.replace(':userId', userId.toString()), { lang: lang })
      .pipe(
        map((resp: { data: MpsUser }) => {
          const user = resp.data;
          this.userSubject.next(user);
          return user;
        })
      );
  }

  private refreshToken(provider: string): Observable<{ data: MpsUser }> {
    return this.oauth2Service.refreshToken(provider).pipe(
      switchMap((response) => {
        localStorage.setItem('token', response.token);

        return this.apiService.get<{ data: MpsUser }>(mpsEndpoints.user);
      }),
      catchError((error: HttpErrorResponse) => {
        this.getProviders();
        return throwError(() => error);
      })
    );
  }

  private getProviders(): void {
    this.oauth2Service.getProviders().subscribe((providers: MpsOauth2Provider[]) => {
      if (providers.length === 1) {
        localStorage.setItem('provider', providers[0].code);
        window.location.href = providers[0].url;
      } else {
        this.router.navigateByUrl(mpsRouteNames.auth + '/' + mpsRouteNames.login);
      }
    });
  }

  private storeUserLanguage(user: MpsUser): void {
    if (user.lang) {
      localStorage.setItem('lang', user.lang);
    }
  }
}
